import React, { useEffect, useMemo, useState } from 'react';
import { StandingsEntry } from '../../../shared/types';
import TeamBreakdown from './TeamBreakdown';
import './Leaderboard.css';
import { LiveGamesService, LiveGame } from '../services/LiveGamesService';
import { getOwnerColor, getOwnerColorClass } from '../utils/ownerColors';
import { getFantasyTeamName } from '../utils/teamNames';

interface LeaderboardProps {
  standings: StandingsEntry[];
  lastUpdated: string;
}

type OwnerAggregateRecord = {
  regularWins: number;
  regularLosses: number;
  regularTies: number;
  playoffWins: number;
  playoffLosses: number;
  playoffByes: number;
};

const Leaderboard: React.FC<LeaderboardProps> = ({ standings, lastUpdated }) => {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [weeklyGames, setWeeklyGames] = useState<LiveGame[]>([]);

  const toggleRow = (rank: number) => {
    setExpandedRow(expandedRow === rank ? null : rank);
  };

  const formatLastUpdated = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Build team -> owner mapping from standings
  const ownerByTeam = useMemo(() => {
    const map: Record<string, string> = {};
    (standings || []).forEach((entry) => {
      (entry.teams || []).forEach((t) => {
        map[t] = entry.name;
      });
    });
    return map;
  }, [standings]);

  // Fetch this week's games and refresh periodically
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const games = await LiveGamesService.fetchThisWeekGames();
        if (mounted) setWeeklyGames(games);
      } catch (e) {
        // Silently ignore load errors for weekly games to avoid UI noise
        // console.warn('Failed to load weekly games', e);
      }
    };
    load();
    const id = setInterval(load, 30 * 1000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  // Compute this week's record per owner from finished games
  const weeklyRecord = useMemo(() => {
    const record: Record<string, { wins: number; losses: number; ties: number }> = {};
    // Initialize all owners to 0-0
    (standings || []).forEach((s) => {
      record[s.name] = { wins: 0, losses: 0, ties: 0 };
    });

    (weeklyGames || [])
      .filter((g) => (g.statusState || '').toLowerCase() === 'post')
      .forEach((g) => {
        const homeOwner = ownerByTeam[g.homeAbbr];
        const awayOwner = ownerByTeam[g.awayAbbr];
        const homeScore = g.homeScore ?? 0;
        const awayScore = g.awayScore ?? 0;

        if (homeScore === awayScore) {
          if (homeOwner) record[homeOwner].ties += 1;
          if (awayOwner) record[awayOwner].ties += 1;
        } else {
          const homeWon = homeScore > awayScore;

          if (homeOwner) {
            if (homeWon) record[homeOwner].wins += 1;
            else record[homeOwner].losses += 1;
          }
          if (awayOwner) {
            if (!homeWon) record[awayOwner].wins += 1;
            else record[awayOwner].losses += 1;
          }
        }
      });

    return record;
  }, [weeklyGames, ownerByTeam, standings]);

  const aggregateRecords = useMemo(() => {
    const map: Record<string, OwnerAggregateRecord> = {};
    (standings || []).forEach((entry) => {
      const totals: OwnerAggregateRecord = {
        regularWins: 0,
        regularLosses: 0,
        regularTies: 0,
        playoffWins: 0,
        playoffLosses: 0,
        playoffByes: 0,
      };

      Object.values(entry.teamRecords || {}).forEach((record) => {
        totals.regularWins += record.regularWins;
        totals.regularLosses += record.regularLosses;
        totals.regularTies += record.regularTies;
        totals.playoffWins += record.playoffWins;
        totals.playoffLosses += record.playoffLosses;
        totals.playoffByes += record.playoffByes || 0;
      });

      map[entry.name] = totals;
    });
    return map;
  }, [standings]);

  return (
    <div className="leaderboard">
      <div className="leaderboard-header">
        <h1>Franchise Fantasy League Standings</h1>
        <p className="last-updated">Last updated: {formatLastUpdated(lastUpdated)}</p>
      </div>

      <div className="standings-table">
        <div className="table-header">
          <div className="col-rank">Rank</div>
          <div className="col-name">Team</div>
          <div className="col-score">Total Score</div>
          <div className="col-week">This Week's Record</div>
          <div className="col-regular">Regular Season Record</div>
          <div className="col-playoff">Playoffs Record</div>
          <div className="col-bonus">Playoff Bonus</div>
          <div className="col-expand"></div>
        </div>

        {standings.map((entry) => {
          const totals = aggregateRecords[entry.name] || {
            regularWins: 0,
            regularLosses: 0,
            regularTies: 0,
            playoffWins: 0,
            playoffLosses: 0,
            playoffByes: 0,
          };
          const weekly = weeklyRecord[entry.name] || { wins: 0, losses: 0, ties: 0 };
          const regularRecord = `${totals.regularWins}-${totals.regularLosses}-${totals.regularTies}`;
          const playoffRecord = `${totals.playoffWins}-${totals.playoffLosses}`;
          const byeCount = entry.playoffByes ?? totals.playoffByes ?? 0;
          const byePoints = entry.playoffByePoints ?? (byeCount * 2);
          const bonusDisplay = byeCount > 0 ? `${byePoints} pts (${byeCount} bye${byeCount === 1 ? '' : 's'})` : '0 pts';

          return (
            <div key={entry.name} className="table-row-container">
              <div 
                className={`table-row ${expandedRow === entry.rank ? 'expanded' : ''}`}
                onClick={() => toggleRow(entry.rank)}
              >
                <div className="col-rank">
                  <span className={`rank-badge rank-${entry.rank}`}>
                    {entry.rank}
                  </span>
                </div>
                <div className="col-name">
                  <span
                    className={`player-name ${getOwnerColorClass(entry.name)}`}
                    style={{ color: getOwnerColor(entry.name) }}
                  >
                    {getFantasyTeamName(entry.name)}
                  </span>
                </div>
                <div className="col-score">
                  <span className="total-score">{entry.totalScore}</span>
                </div>
                <div className="col-week">
                  <span>
                    {`${weekly.wins}-${weekly.losses}-${weekly.ties}`}
                  </span>
                </div>
                <div className="col-regular">
                  <span>{regularRecord}</span>
                </div>
                <div className="col-playoff">
                  <span>{playoffRecord}</span>
                </div>
                <div className="col-bonus">
                  <span>{bonusDisplay}</span>
                </div>
                <div className="col-expand">
                  <span className={`expand-icon ${expandedRow === entry.rank ? 'rotated' : ''}`}>
                    ▼
                  </span>
                </div>
              </div>

              {expandedRow === entry.rank && (
                <div className="expanded-content">
                  <TeamBreakdown 
                    playerName={entry.name}
                    teams={entry.teams}
                    teamRecords={entry.teamRecords}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Leaderboard;
